import { Location } from "@angular/common";
import { Component, OnInit, OnDestroy, inject } from "@angular/core";
import { Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { LegacyPageEvent as PageEvent } from "@angular/material/legacy-paginator";

import { AppState } from "@store/models/state.model";
import { getCustomerEntityGroupState, getGlobalDashboardFilterState } from "@store/index";
import { SetStatusAction } from "@store/actions/statusNetPayment.action";
import { PayGroup } from "@shared/models/pay-groups";
import { Milestone, PayCycle } from "@shared/models/pay-cycle.interface";
import { ToastService } from "@shared/services/toast/toast.service";
import { MenuService } from "@modules/config/_services/menu/menu.service";
import { NetPaymentReportPagination } from "@shared/models/net-payments.interface";
import { MilestoneCardData } from "@shared/models/milestone-cards.interface";
import { NetPaymentOrdersService } from "@shared/services/net-payment/net-payment-orders.service";
import { Pagination } from "@shared/models/pagination.interface";
import { CommonService } from "@modules/dashboard/services/common.service";
import { ExternalCrumb } from "@shared/models/breadcrumbs.interface";
import { UpdateHistoryStateInfoAction } from "@store/actions/historyStateInfo.action";
import { FilterMilestoneTypes } from "@modules/global-dashboard/models/global-dashboard-interface";

@Component({
	selector: "app-net-payment-orders",
	templateUrl: "./net-payment-orders.component.html",
	styleUrls: ["./net-payment-orders.component.scss"]
})
export class NetPaymentOrdersComponent implements OnInit, OnDestroy {
	payGroup!: PayGroup;
	payCycle!: PayCycle;
	payCycleId = "";
	payrollCycleId = "";
	payCycleMilestones!: Milestone[];

	milestoneCardData!: MilestoneCardData[];
	paymentOrdersPagination!: NetPaymentReportPagination;

	milestone!: Milestone;

	kpiSelected = "";
	payCycleName = "";
	title = "NET Payment Orders / ";
	subtitle = "";
	milestoneGroup = "NETS";
	flag = "GP";
	isPaymentOrderMenuOpen = false;
	paygroupId = "";

	pagination: Pagination = {
		pageSize: 20,
		pageIndex: 0,
		totalPageCount: 0
	};

	isCPNowDeductionsEnabled = false;

	/* Breadcrumbs */
	headerCrumb = "";
	externalCrumbs: ExternalCrumb[] = [];

	private destroy$: Subject<void> = new Subject<void>();

	private store = inject(Store<AppState>);
	private router = inject(Router);
	private menuService = inject(MenuService);
	private toast = inject(ToastService);
	private netPaymentOrdersService = inject(NetPaymentOrdersService);
	private readonly location = inject(Location);
	private commonService = inject(CommonService);

	constructor() {}

	ngOnInit(): void {
		this.getSelectedKPI();
		this.menuService.setVisible(false);
		this.store.pipe(takeUntil(this.destroy$), select(getCustomerEntityGroupState)).subscribe(customerState => {
			this.paygroupId = customerState.payGroupId;
		});
		if (history.state && history.state.payGroup) {
			this.isCPNowDeductionsEnabled = history.state.payGroup.data.cpNowDeductionsEnabled;
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Gets value of nets payment order card
	 * @param milestoneId: milestone ID
	 */
	getNetPaymentOrderCardData(milestoneId: string): void {
		this.netPaymentOrdersService
			.getNetPaymentKpiCardData(milestoneId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				this.milestoneCardData = res;

				if (!this.isCPNowDeductionsEnabled) {
					this.milestoneCardData = this.milestoneCardData.filter(card => card.key !== "totalDeductionAmount");
				}
			});
	}

	getNetPaymentOrders(employeeQueryString = "", pageIndex?: number): void {
		const milestoneId = this.milestone.id ?? "";

		this.netPaymentOrdersService
			.getNetPaymentOrders(
				milestoneId,
				pageIndex ?? this.pagination.pageIndex,
				this.pagination.pageSize,
				employeeQueryString
			)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe(paymentOrdersPagination => {
				this.paymentOrdersPagination = paymentOrdersPagination;
				this.pagination.totalPageCount = paymentOrdersPagination.totalCount;
			});
	}

	getNetPaymentOrdersBySearch(employeeId: string): void {
		this.getNetPaymentOrders(employeeId, 0);
	}

	getSelectedKPI(): void {
		this.store.pipe(takeUntil(this.destroy$), select(getGlobalDashboardFilterState)).subscribe(state => {
			//if coming from dashboard failed transactions
			if (state?.globalDashboard?.kpiSelected !== undefined) {
				this.kpiSelected = state.globalDashboard.kpiSelected;
				if (state.globalDashboard.kpiSelected === "Failed Transactions") {
					this.title = "Failed transactions / ";
					this.store.dispatch(
						new SetStatusAction({
							statusPayment: ["FAILED,ATTEMPTS_LIMIT_EXCEEDED", "FAILED_TO_BOOK", "PROCESSING,PENDING"]
						})
					);
				}
				this.setHistory();
			} else {
				this.router.navigate(["/global-dashboard"]);
			}
		});
	}

	setHistory(): void {
		if (history?.state?.payCycleId && history?.state?.payGroup && history?.state?.milestone) {
			this.payGroup = history.state.payGroup;
			this.payCycle = history.state.payCycle;
			this.payCycleName = history.state.payCycle.name;
			this.payCycleId = history.state.payCycleId;
			this.payCycleMilestones = history.state.milestones;
			this.milestone = history.state.milestone;
			this.payrollCycleId = history.state.payCycle.payrollCycleId;

			if (this.payGroup?.data && this.payGroup?.legalEntity?.data && this.milestone?.group) {
				this.setupTopCardInfo();
			}

			if (this.milestone.id) {
				this.getNetPaymentOrderCardData(this.milestone.id);
				this.getNetPaymentOrders();
			}

			this.headerCrumb = history.state.payrollCycleId
				? `${history.state.payGroup.customer.name} - ${history.state.payCycle.name} - ${history.state.payrollCycleId}, GROUP ${history.state.milestone?.group}`
				: `${history.state.payGroup.customer.name} - ${history.state.payCycle.name}, GROUP ${history.state.milestone?.group}`;

			this.externalCrumbs = [
				{
					crumb: "Payment Orders",
					dropDownData: [
						{
							id: this.payCycleMilestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.FUNDING &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Funding Date"
						},
						{
							id: this.payCycleMilestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.PAYMENTS &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Net Payment"
						}
					]
				}
			];
		} else {
			this.location.historyGo(-1);
			this.toast.showError("Something went wrong while trying to load the page. Please try again later.");
		}
	}

	/**
	 * Route user to other milestone pages
	 * @param id: milestone ID
	 */
	routeToMilestonePage(id?: string): void {
		const selectedMilestone = this.payCycleMilestones.find(milestone => milestone.id === id)!;
		let route = "global-dashboard";

		switch (selectedMilestone.type) {
			case FilterMilestoneTypes.FUNDING:
				this.router.navigate([`/${route}/funding-date`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						payCycle: this.payCycle,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId,
						previousUrl: this.router.url
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case FilterMilestoneTypes.PAYMENTS:
				this.router.navigate([`/${route}/payouts`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						payCycle: this.payCycle,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.payCycleMilestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			default:
				break;
		}
	}

	setupTopCardInfo(): void {
		this.title = this.title + this.payGroup.data.name;
		this.subtitle = this.commonService.formatMilestonePagesSubtitles(
			this.payGroup.legalEntity.data.name,
			this.payCycleName,
			this.payrollCycleId,
			this.milestone.group
		);
		this.flag = this.payGroup.legalEntity.data.country;
		this.milestoneGroup = this.milestone.group;
	}

	refreshData(): void {
		if (!this.milestone.id) return;

		this.getNetPaymentOrderCardData(this.milestone.id);
		this.getNetPaymentOrders();
	}

	paginate(pagination: PageEvent): void {
		this.pagination.pageSize = pagination.pageSize;
		this.pagination.pageIndex = pagination.pageIndex;
		this.getNetPaymentOrders();
	}

	goToFileHistory(): void {
		const route = this.router.url === "/global-dashboard/payment-orders" ? "global-dashboard" : "payments";

		this.router.navigate([`/${route}/file-history`], {
			state: {
				payGroup: this.payGroup,
				payCycleId: this.payCycle.id,
				payCycleExternalId: this.payCycle.externalId,
				payCycle: this.payCycle,
				milestone: this.milestone,
				milestones: this.payCycleMilestones,
				payrollCycleId: this.payCycle.payrollCycleId,
				previousUrl: this.router.url
			}
		});
	}

	refreshPaymentOrders(): void {
		throw new Error("Method not implemented.");
	}
}
